import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { X } from 'lucide-react';

const ANY = 'any';
const field = 'bg-[#080d18] border-slate-700 text-white h-9';

const Pick = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label className="text-slate-400 text-[11px] uppercase tracking-wider mb-1 block">{label}</Label>
    <Select value={value || ANY} onValueChange={v => onChange(v === ANY ? null : v)}>
      <SelectTrigger data-testid={testId} className={field}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-[#0d1424] border-slate-700 text-white max-h-72">
        <SelectItem value={ANY}>Any</SelectItem>
        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export const EMPTY_FILTERS = { source: null, job_id: null, date_from: '', date_to: '', min_clearance: null, certifications: [], min_years: '', max_years: '', location: '', min_education: null };

export const ResumeFilters = ({ filters, setFilters, facets }) => {
  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const toggleCert = (c) => set('certifications', filters.certifications.includes(c) ? filters.certifications.filter(x => x !== c) : [...filters.certifications, c]);
  const active = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  return (
    <aside data-testid="resume-filters" className="rounded-xl border border-cyan-400/10 bg-slate-900/30 p-5 space-y-4 h-fit">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-white">Filters</h4>
        {active && <Button data-testid="resume-filters-clear" variant="ghost" onClick={() => setFilters(EMPTY_FILTERS)} className="h-7 px-2 text-xs text-slate-400 hover:text-white hover:bg-slate-800"><X className="w-3 h-3 mr-1" />Clear</Button>}
      </div>
      <Pick label="Source" testId="filter-source" value={filters.source} onChange={v => set('source', v)} options={[{ value: 'application', label: 'Applied on website' }, { value: 'import', label: 'Imported' }]} />
      <Pick label="Job" testId="filter-job" value={filters.job_id} onChange={v => set('job_id', v)} options={[{ value: 'none', label: 'Not linked to a job' }, ...(facets?.jobs || []).map(j => ({ value: j.id, label: `${j.title} (${j.count})` }))]} />
      <div>
        <Label className="text-slate-400 text-[11px] uppercase tracking-wider mb-1 block">Date added</Label>
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="filter-date-from" type="date" value={filters.date_from} onChange={e => set('date_from', e.target.value)} className={field} />
          <Input data-testid="filter-date-to" type="date" value={filters.date_to} onChange={e => set('date_to', e.target.value)} className={field} />
        </div>
      </div>
      <Pick label="Minimum clearance" testId="filter-clearance" value={filters.min_clearance} onChange={v => set('min_clearance', v)} options={(facets?.clearances || []).filter(c => c !== 'None').map(c => ({ value: c, label: `${c} or higher` }))} />
      <Pick label="Minimum education" testId="filter-education" value={filters.min_education} onChange={v => set('min_education', v)} options={(facets?.education_levels || []).map(c => ({ value: c, label: `${c} or higher` }))} />
      <div>
        <Label className="text-slate-400 text-[11px] uppercase tracking-wider mb-1 block">Years of experience</Label>
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="filter-min-years" type="number" min="0" placeholder="Min" value={filters.min_years} onChange={e => set('min_years', e.target.value)} className={field} />
          <Input data-testid="filter-max-years" type="number" min="0" placeholder="Max" value={filters.max_years} onChange={e => set('max_years', e.target.value)} className={field} />
        </div>
      </div>
      <div>
        <Label className="text-slate-400 text-[11px] uppercase tracking-wider mb-1 block">Candidate location</Label>
        <Input data-testid="filter-location" placeholder="e.g. VA or Chantilly" value={filters.location} onChange={e => set('location', e.target.value)} className={field} />
      </div>
      <div>
        <Label className="text-slate-400 text-[11px] uppercase tracking-wider mb-1.5 block">Certifications (has all selected)</Label>
        {(facets?.certifications || []).length === 0 ? <p className="text-xs text-slate-500">None found yet.</p> : (
          <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
            {facets.certifications.map(c => {
              const on = filters.certifications.includes(c.name);
              return (
                <button key={c.name} type="button" data-testid={`filter-cert-${c.name}`} onClick={() => toggleCert(c.name)}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${on ? 'bg-cyan-400 text-slate-900 border-cyan-400' : 'border-slate-700 text-slate-300 hover:border-cyan-400/60'}`}>
                  {c.name} <span className={on ? 'text-slate-700' : 'text-slate-500'}>{c.count}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
};

export default ResumeFilters;
